import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  TablePagination,
} from "@mui/material";
import {
  Refresh,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  useForecastingExecutionLogsQuery,
  ExecutionStatus,
} from "../../../graphql/generated";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import { testIds } from "./ExecutionHistoryView.testIds";

interface ExecutionHistoryViewProps {
  receiverForecastId: string;
}

// Helper function to calculate duration in seconds
const calculateDuration = (
  startedAt: string,
  completedAt?: string | null,
): number | null => {
  if (!completedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  return Math.floor((end - start) / 1000); // Duration in seconds
};

// Helper function to format duration
const formatDuration = (durationInSeconds: number | null): string => {
  if (durationInSeconds === null) return "-";

  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = durationInSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

// Helper function to get status badge color and label
const getStatusBadge = (status: ExecutionStatus) => {
  switch (status) {
    case ExecutionStatus.Success:
      return { color: "success" as const, label: "Success" };
    case ExecutionStatus.Failed:
      return { color: "error" as const, label: "Failed" };
    case ExecutionStatus.InProgress:
      return { color: "info" as const, label: "In Progress" };
    default:
      return { color: "default" as const, label: status };
  }
};

function ExecutionHistoryView({
  receiverForecastId,
}: ExecutionHistoryViewProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Calculate skip for server-side pagination
  const skip = page * rowsPerPage;

  // Query for execution history with server-side pagination
  const executionHistoryQuery = useForecastingExecutionLogsQuery({
    receiverForecastId,
    take: rowsPerPage,
    skip,
  });

  const executionLogs =
    executionHistoryQuery.data?.forecastingExecutionLogs.data || [];
  const totalCount =
    executionHistoryQuery.data?.forecastingExecutionLogs.count || 0;

  const handleRefresh = () => {
    executionHistoryQuery.refetch();
  };

  const toggleRowExpansion = (logId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <Paper sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Execution History</Typography>
        <IconButton data-testid={testIds.executionHistoryViewRefresh}
          onClick={handleRefresh}
          disabled={executionHistoryQuery.isLoading}
          title="Refresh"
        >
          {executionHistoryQuery.isLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Refresh />
          )}
        </IconButton>
      </Box>

      {executionHistoryQuery.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load execution history
        </Alert>
      )}

      {executionHistoryQuery.isLoading && !executionLogs.length && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!executionHistoryQuery.isLoading && executionLogs.length === 0 && (
        <Alert severity="info">No execution history available</Alert>
      )}

      {executionLogs.length > 0 && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Orders Generated</TableCell>
                  <TableCell align="right">Locked Orders</TableCell>
                  <TableCell align="right">Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executionLogs.map((log) => {
                  const statusBadge = getStatusBadge(log.status);
                  const duration = calculateDuration(
                    log.startedAt,
                    log.completedAt,
                  );
                  const isExpanded = expandedRows.has(log._id);
                  const hasError =
                    log.status === ExecutionStatus.Failed && log.errorMessage;

                  return (
                    <React.Fragment key={log._id}>
                      <TableRow hover>
                        <TableCell>
                          {hasError && (
                            <IconButton data-testid={`${testIds.executionHistoryViewToggleErrorDetails}-${log._id}`}
                              size="small"
                              onClick={() => toggleRowExpansion(log._id)}
                              aria-label={isExpanded ? "collapse row" : "expand row"}
                            >
                              {isExpanded ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(log.startedAt, null, false)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusBadge.label}
                            color={statusBadge.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {log.ordersGenerated ?? "-"}
                        </TableCell>
                        <TableCell align="right">
                          {log.lockedOrdersCount ?? "-"}
                        </TableCell>
                        <TableCell align="right">
                          {formatDuration(duration)}
                        </TableCell>
                      </TableRow>
                      {hasError && (
                        <TableRow>
                          <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={6}
                          >
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ margin: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  component="div"
                                >
                                  Error Details
                                </Typography>
                                {log.errorMessage && (
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      gutterBottom
                                    >
                                      Message:
                                    </Typography>
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                      {log.errorMessage}
                                    </Alert>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Paper>
  );
}

export default ExecutionHistoryView;
